import React, { Fragment, ReactNode, useEffect, useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
    Collapse,
    Drawer,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Skeleton,
    Typography,
    styled,
    useTheme,
} from '@mui/material';
import Icon from '@mdi/react';
import {
    mdiChevronDown,
    mdiChevronUp,
    mdiCompass,
    mdiDomain,
    mdiEarth,
    mdiMedicalBag,
    mdiMenu,
    mdiMortarPestlePlus,
    mdiVectorPolyline,
} from '@mdi/js';

import { useAppSelector } from '../../state/hooks';
import { privateRoutes } from '../../routes/appRouteList';
import { hasNetworkRxRole } from '../../utils/environmentUtils';

const classesPrefix = 'navMenu';

const classes = {
    drawer: `${classesPrefix}-drawer`,
    drawerPaper: `${classesPrefix}-drawerPaper`,
    listItem: `${classesPrefix}-listItem`,
    parentListItem: `${classesPrefix}-parentListItem`,
    subListItem: `${classesPrefix}-subListItem`,
    listItemSelected: `${classesPrefix}-listItemSelected`,
    versionContainer: `${classesPrefix}-versionContainer`,
};

const StyledDrawer = styled(Drawer)(({ theme }) => {
    return {
        [`& .${classes.drawer}`]: {
            zIndex: theme.zIndex.appBar - 2,
            position: 'absolute',
        },
        [`& .${classes.drawerPaper}`]: {
            display: 'flex',
            justifyContent: 'space-between',
            width: '270px',
            position: 'absolute',
            height: '100%',
            top: 0,
        },
        [`& .${classes.listItem}`]: {
            color: theme.palette.text.primary,
            '& .MuiListItemIcon-root': {
                color: theme.palette.secondary.main,
            },
            '&:hover': {
                '& .MuiListItemIcon-root': {
                    color: theme.palette.secondary.main,
                },
            },
        },
        [`& .${classes.parentListItem}`]: {
            padding: 0,
        },
        [`& .${classes.subListItem}`]: {
            paddingLeft: theme.spacing(5),
        },
        [`& .${classes.listItemSelected}`]: {
            color: theme.palette.primary.main,
            '&.Mui-selected': {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.common.white,
                '& .MuiListItemText-root': {
                    color: theme.palette.common.white,
                },
                '&:hover': {
                    backgroundColor: theme.palette.secondary.light,
                },
                '& .MuiListItemIcon-root': {
                    color: 'inherit',
                },
            },
            '& .MuiListItemIcon-root': {
                color: 'inherit',
            },
        },
        [`& .${classes.versionContainer}`]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '8px',
        },
    };
});

enum ParentName {
    Explore = 'explore',
    HealthAssesment = 'healthAssessment',
    NetworkRx = 'networkRx',
}

interface NavPage {
    name: ReactNode;
    path: string;
    icon: string;
}

interface NavPageItem extends NavPage {
    openName: ParentName;
    selectable: boolean;
    disabled: boolean;
    tooltipText?: string;
    subPages: NavPage[];
}

const NavMenu = (): React.JSX.Element => {
    const location = useLocation();
    const theme = useTheme();

    const [isAppNavMenuOpen, setIsAppNavMenuOpen] = useState(false);
    const [isParentItemOpen, setIsParentItemOpen] = useState<Record<ParentName, boolean>>({
        [ParentName.Explore]: true,
        [ParentName.HealthAssesment]: true,
        [ParentName.NetworkRx]: true,
    });
    const [version, setVersion] = useState<{ version: string | null; versionLoading: boolean; }>({ version: null, versionLoading: true });

    const userLoginStatus = useAppSelector((state) => { return state.user.userLoginStatus; });
    const userDetails = useAppSelector((state) => { return state.user.userDetails; });

    useEffect(() => {
        axios
            .get('./version.json')
            .then((resp: AxiosResponse<{ build: string; version: string; }>) => {
                setVersion({
                    version: resp.data.build,
                    versionLoading: false,
                });
            })
            .catch((e) => {
                console.error(e);
                setVersion({
                    version: null,
                    versionLoading: false,
                });
            });
    }, []);

    const hasNetworkRxPermission = hasNetworkRxRole({ userLoginStatus, userDetails });

    const corePages: NavPageItem[] = [
        {
            name: 'Explore',
            openName: ParentName.Explore,
            path: privateRoutes.lanes,
            icon: mdiCompass,
            selectable: false,
            disabled: false,
            subPages: [
                {
                    name: 'Lanes',
                    path: privateRoutes.lanes,
                    icon: mdiVectorPolyline,
                },
                {
                    name: 'Shippers',
                    path: privateRoutes.shippers,
                    icon: mdiDomain,
                },
            ],
        },
        {
            name: 'Health Assessment',
            openName: ParentName.HealthAssesment,
            path: privateRoutes.healthAssessment,
            icon: mdiMedicalBag,
            selectable: true,
            disabled: false,
            subPages: [
                {
                    name: 'Geography',
                    path: privateRoutes.areaHealth,
                    icon: mdiEarth,
                },
                {
                    name: 'Shippers',
                    path: privateRoutes.shipperHealth,
                    icon: mdiDomain,
                },
            ],
        },
    ];

    const networkRxMenuItem: NavPageItem = {
        name: (
            <Fragment>
                <span>Network Rx </span>
                <sup>BETA</sup>
            </Fragment>
        ),
        openName: ParentName.NetworkRx,
        path: privateRoutes.networkRx,
        icon: mdiMortarPestlePlus,
        selectable: true,
        disabled: !hasNetworkRxPermission,
        tooltipText: !hasNetworkRxPermission ? 'Purchase Network Rx to unlock more insights' : undefined,
        subPages: [],
    };

    const pages = hasNetworkRxPermission ? [...corePages, networkRxMenuItem] : corePages;

    const handlePageLinkClick = (): void => {
        setIsAppNavMenuOpen(false);
    };

    const renderVersion = (): string | React.JSX.Element => {
        if (version.versionLoading) {
            return <Skeleton />;
        }

        return version.version ? `Version: ${version.version}` : '';
    };

    const renderListItem = (route: NavPageItem): React.JSX.Element => {
        const isRouteSelected = route.selectable ? route.path === location.pathname : false;
        return (
            <ListItem
                className={classes.listItem}
                classes={{
                    root: classes.parentListItem,
                }}
                disablePadding
                secondaryAction={
                    route.subPages.length > 0 ?
                        <IconButton
                            edge='end'
                            onClick={(event): void => {
                                event.stopPropagation();
                                event.preventDefault();
                                setIsParentItemOpen((prevState) => {
                                    return {
                                        ...prevState,
                                        [route.openName]: !prevState[route.openName],
                                    };
                                });
                            }}
                        >
                            <Icon
                                path={isParentItemOpen[route.openName] ? mdiChevronUp : mdiChevronDown}
                                size={1}
                                color={isRouteSelected ? theme.palette.common.white : theme.palette.text.primary}
                            />
                        </IconButton> :
                        undefined
                }
                onClick={handlePageLinkClick}
            >
                <ListItemButton
                    selected={isRouteSelected}
                    disabled={route.disabled}
                    classes={{
                        selected: classes.listItemSelected,
                    }}
                >
                    <ListItemIcon>
                        <Icon path={route.icon} size={1} />
                    </ListItemIcon>
                    <ListItemText primary={route.name} />
                </ListItemButton>
            </ListItem>
        );
    };

    return (
        <Fragment>
            <Tooltip title='Menu'>
                <IconButton
                    data-testid='menu-button'
                    edge='start'
                    color='inherit'
                    onClick={(): void => {
                        setIsAppNavMenuOpen((isOpen) => {
                            return !isOpen;
                        });
                    }}
                >
                    <Icon path={mdiMenu} size={1} />
                </IconButton>
            </Tooltip>
            <StyledDrawer
                data-testid='side-nav-menu'
                style={{
                    position: 'absolute',
                    top: '64px',
                    zIndex: 1099,
                }}
                classes={{
                    root: classes.drawer,
                    paper: classes.drawerPaper,
                }}
                open={isAppNavMenuOpen}
                onClose={(): void => {
                    setIsAppNavMenuOpen(false);
                }}
            >
                <List>
                    {
                        pages.map((route) => {
                            return (
                                <Fragment key={route.path}>
                                    <Tooltip title={route.tooltipText}>
                                        {
                                            route.disabled ?
                                                renderListItem(route) :
                                                <Link
                                                    underline='none'
                                                    component={RouterLink}
                                                    to={route.path}
                                                >
                                                    {renderListItem(route)}
                                                </Link>
                                        }
                                    </Tooltip>
                                    {
                                        route.subPages.length > 0 &&
                                        <Collapse in={isParentItemOpen[route.openName]} timeout='auto'>
                                            <List component='div' disablePadding>
                                                {
                                                    route.subPages.map((subRoute) => {
                                                        return (
                                                            <Link
                                                                key={subRoute.path}
                                                                underline='none'
                                                                component={RouterLink}
                                                                to={subRoute.path}
                                                            >
                                                                <ListItemButton
                                                                    className={classes.listItem}
                                                                    classes={{
                                                                        root: classes.subListItem,
                                                                        selected: classes.listItemSelected,
                                                                    }}
                                                                    selected={subRoute.path === location.pathname}
                                                                    onClick={handlePageLinkClick}
                                                                >
                                                                    <ListItemIcon>
                                                                        <Icon path={subRoute.icon} size={1} />
                                                                    </ListItemIcon>
                                                                    <ListItemText primary={subRoute.name} />
                                                                </ListItemButton>
                                                            </Link>
                                                        );
                                                    })
                                                }
                                            </List>
                                        </Collapse>
                                    }
                                </Fragment>
                            );
                        })
                    }
                </List>
                <div className={classes.versionContainer}>
                    <Typography variant='caption'>
                        {renderVersion()}
                    </Typography>
                    <Typography variant='body1'>&copy; 2025 Trimble, Inc</Typography>
                    <Typography variant='body1'>All rights reserved</Typography>
                </div>
            </StyledDrawer>
        </Fragment>
    );
};

export default NavMenu;
